/**
 * @typedef {Object} ParsedImprovement
 * @property {string} id
 * @property {string} [emoji]
 * @property {string} title
 * @property {string} description
 * @property {string} [category]
 * @property {string} [previewType]
 * @property {string} [articleUrl]
 * @property {string} [articleText]
 * @property {string} [actionUrl]
 * @property {string} [actionText]
 */

/**
 * @typedef {Object} ParseImprovementsOptions
 * @property {string[]} [tags]
 */

const FALLBACK_TITLE = "Destaques desta Versão";

/** @type {{ category: string, previewType: string, keywords: string[] }[]} */
const INFERENCE_RULES = [
  {
    category: "Dados & Integrações Analíticas",
    previewType: "odata",
    keywords: ["odata", "entity set", "entityset", "$filter", "csdl"]
  },
  {
    category: "Dados & Integrações Analíticas",
    previewType: "mcp",
    keywords: [
      "mcp",
      "model context protocol",
      "query curious",
      "querycurious",
      "agente ia",
      "agente de ia"
    ]
  },
  {
    category: "BI & Data Warehouse",
    previewType: "analytics",
    keywords: [
      "data warehouse",
      "warehouse",
      "power bi",
      "powerbi",
      "indicadores",
      "dashboard analitico",
      "dashboard analítico",
      "dw"
    ]
  },
  {
    category: "Hubs de Frete & Entrega",
    previewType: "freight",
    keywords: [
      "hub de frete",
      "hubs de frete",
      "frete",
      "transportadora",
      "cotacao de frete",
      "cotação de frete",
      "melhor envio",
      "intelipost",
      "kangu",
      "logistica reversa",
      "logística reversa"
    ]
  },
  {
    category: "Operações & Logística",
    previewType: "tracking",
    keywords: [
      "rastreamento",
      "rastreio",
      "tracking",
      "codigo de rastreio",
      "código de rastreio",
      "status de entrega"
    ]
  },
  {
    category: "Marketing & Fidelização",
    previewType: "cashback",
    keywords: [
      "cashback",
      "cash back",
      "fidelidade",
      "programa de pontos",
      "pontos",
      "recompensa"
    ]
  },
  {
    category: "Atendimento & LifeChat",
    previewType: "tags",
    keywords: [
      "etiqueta",
      "lifechat",
      "life chat",
      "atendimento",
      "conversa",
      "whatsapp"
    ]
  },
  {
    category: "Catálogo & Integração ERP",
    previewType: "images",
    keywords: [
      "imagem",
      "imagens",
      "catalogo",
      "catálogo",
      "erp",
      "sku",
      "produto",
      "sincronizacao de produto",
      "sincronização de produto",
      "winthor",
      "paje"
    ]
  },
  {
    category: "Operações & Logística",
    previewType: "distribution",
    keywords: [
      "distribui",
      "separacao",
      "separação",
      "picking",
      "expedicao",
      "expedição",
      "wms",
      "estoque"
    ]
  },
  {
    category: "Segurança & Acessos",
    previewType: "permission",
    keywords: [
      "permissao",
      "permissão",
      "seguranca",
      "segurança",
      "acesso",
      "keycloak",
      "oauth",
      "sso",
      "perfil de usuario",
      "perfil de usuário"
    ]
  },
  {
    category: "Gestão de Vendas",
    previewType: "cart",
    keywords: [
      "carrinho",
      "aviso",
      "orcamento",
      "orçamento",
      "pedido",
      "checkout"
    ]
  },
  {
    category: "Financeiro & Pagamento",
    previewType: "payment",
    keywords: [
      "pagamento",
      "plano",
      "pix",
      "boleto",
      "cartao",
      "cartão",
      "condicao de pagamento",
      "condição de pagamento",
      "safra",
      "antecipacao",
      "antecipação"
    ]
  },
  {
    category: "Marketing & Conversão",
    previewType: "coupon",
    keywords: [
      "cupom",
      "desconto",
      "primeira compra",
      "promocao",
      "promoção",
      "campanha"
    ]
  },
  {
    category: "Integrações & Mensageria",
    previewType: "integration",
    keywords: ["kafka", "webhook", "api v5", "integracao", "integração"]
  },
  {
    category: "LifeGrowth & Equipe Comercial",
    previewType: "sales-tools",
    keywords: [
      "lifegrowth",
      "vendedor",
      "equipe comercial",
      "meta de vendas",
      "objetivo comercial"
    ]
  }
];

const DEFAULT_INFERENCE = {
  category: "Melhoria de Sistema",
  previewType: "generic"
};

function normalizeForMatch(text) {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{M}/gu, "");
}

function buildInferenceHaystack(title, tags = []) {
  const parts = [title, ...tags].filter(Boolean);
  return normalizeForMatch(parts.join(" "));
}

function haystackTokens(haystack) {
  return haystack.split(/[^a-z0-9]+/).filter(Boolean);
}

function haystackMatchesKeywords(haystack, keywords) {
  const tokens = haystackTokens(haystack);
  return keywords.some(kw => {
    const k = normalizeForMatch(kw);
    if (k.length <= 3) {
      return tokens.includes(k);
    }
    return haystack.includes(k);
  });
}

/**
 * @param {string} title
 * @param {string[]} [tags]
 */
export function inferCategoryAndPreview(title, tags = []) {
  const haystack = buildInferenceHaystack(title, tags);
  for (const rule of INFERENCE_RULES) {
    if (haystackMatchesKeywords(haystack, rule.keywords)) {
      return {
        category: rule.category,
        previewType: rule.previewType
      };
    }
  }
  return { ...DEFAULT_INFERENCE };
}

function extractLinks(trimmed) {
  let articleUrl;
  let articleText;
  let actionUrl;
  let actionText;
  const linkRegex = /\[(.*?)\]\((.*?)\)/g;
  let linkMatch;
  while ((linkMatch = linkRegex.exec(trimmed)) !== null) {
    const text = linkMatch[1];
    const url = linkMatch[2];
    if (
      text.toLowerCase().includes("artigo") ||
      text.toLowerCase().includes("acessar")
    ) {
      articleUrl = url;
      articleText = text;
    } else {
      actionUrl = url;
      actionText = text;
    }
  }
  return { articleUrl, articleText, actionUrl, actionText };
}

/**
 * @param {string} content
 * @param {ParseImprovementsOptions} [options]
 * @returns {ParsedImprovement[]}
 */
export function parseImprovementsFromContent(content, options = {}) {
  const tags = options.tags || [];
  const improvements = [];
  const lines = (content || "").split("\n");

  let current = null;
  let descriptionLines = [];

  const flushCurrent = () => {
    if (current && current.title) {
      current.description = descriptionLines.join(" ").trim();
      const inferred = inferCategoryAndPreview(current.title, tags);
      current.category = inferred.category;
      current.previewType = inferred.previewType;
      improvements.push(current);
      current = null;
      descriptionLines = [];
    }
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    const itemMatch = trimmed.match(
      /^([\p{Emoji}\u200d\uFE0F\uFE0E]+|\*\*)?\s*(\*\*)?([^:*]+?)(\*\*)?:\s*(.*)$/u
    );

    if (
      itemMatch &&
      (itemMatch[1] || itemMatch[2] || itemMatch[4] || trimmed.includes("**"))
    ) {
      flushCurrent();

      const rawEmoji = itemMatch[1]
        ? itemMatch[1].replace(/\*/g, "").trim()
        : "";
      const rawTitle = itemMatch[3].replace(/\*\*/g, "").trim();
      const restOfLine = itemMatch[5].trim();
      const links = extractLinks(trimmed);

      current = {
        id: `imp-${improvements.length + 1}`,
        emoji: rawEmoji || "✨",
        title: rawTitle,
        ...links
      };

      if (restOfLine) {
        descriptionLines.push(restOfLine);
      }
    } else if (current) {
      const links = extractLinks(trimmed);
      if (links.articleUrl) {
        current.articleUrl = links.articleUrl;
        current.articleText = links.articleText;
      }
      if (links.actionUrl) {
        current.actionUrl = links.actionUrl;
        current.actionText = links.actionText;
      }
      const cleanLine = trimmed.replace(/\[(.*?)\]\(.*?\)/g, "$1");
      descriptionLines.push(cleanLine);
    }
  }

  flushCurrent();

  if (improvements.length === 0 && content && content.trim()) {
    const inferred = inferCategoryAndPreview(FALLBACK_TITLE, tags);
    improvements.push({
      id: "imp-default",
      emoji: "🚀",
      title: FALLBACK_TITLE,
      category:
        inferred.category === DEFAULT_INFERENCE.category
          ? "Atualização Geral"
          : inferred.category,
      description: content.replace(/\[(.*?)\]\(.*?\)/g, "$1").trim(),
      previewType: inferred.previewType
    });
  }

  return improvements;
}

export { FALLBACK_TITLE };
